import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { authApi } from '../services/authApi';
import { getUserData, isAuthenticated } from '../services/api';
import { LoginCredentials, RegisterData, ClienteProfile } from '../types/auth';
import { ErrorHandler } from '../utils/errorHandler';

// Hook principal
export const useAuth = () => {
  const { data: authData } = useAuthCheck();
  return {
    user: (authData as any)?.user || getUserData(),
    cliente: (authData as any)?.cliente || null,
    isAuthenticated: !!(authData as any)?.user || isAuthenticated(),
  };
};

// Hook para verificar autenticação
export const useAuthCheck = () => {
  return useQuery(
    'auth-check',
    authApi.checkAuth,
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
      enabled: isAuthenticated(), // Só verificar se há token
    }
  );
};

// Hook para login
export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (credentials: LoginCredentials) => authApi.login(credentials),
    {
      onSuccess: (data) => {
        if (data.success) {
          queryClient.setQueryData('auth-check', data);
          toast.success('Login realizado com sucesso!');
          
          // Redirecionar baseado no tipo de usuário
          if (data.user?.tipo === 'cliente') {
            navigate('/my-recommendations'); // Área do catálogo para clientes
          } else if (data.user?.tipo === 'concessionaria') {
            navigate('/properties'); // Área de gestão para concessionárias
          } else {
            navigate('/'); // Fallback
          }
        }
      },
      onError: (error: any) => {
        const errorMessage = ErrorHandler.handleApiError(error);
        toast.error(errorMessage);
      },
    }
  );
};

// Hook para registro
export const useRegister = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (data: RegisterData) => authApi.register(data),
    {
      onSuccess: (data: any) => {
        if (data?.user?.tipo === 'cliente') {
          navigate('/complete-profile');
        } else {
          queryClient.setQueryData('auth-check', data);
          toast.success('Registro realizado com sucesso!');
          navigate('/dashboard');
        }
      },
      onError: (error: any) => {
        const errorMessage = ErrorHandler.handleApiError(error);
        toast.error(errorMessage);
      },
    }
  );
};

// Hook para logout
export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    authApi.logout,
    {
      onSuccess: () => {
        queryClient.clear();
        toast.success('Logout realizado com sucesso!');
        navigate('/');
      },
      onError: (error: any) => {
        const errorMessage = ErrorHandler.handleApiError(error);
        toast.error(errorMessage);
      },
    }
  );
};

// Hook para atualizar perfil
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Partial<ClienteProfile>) => authApi.updateProfile(data),
    {
      onSuccess: (data) => {
        queryClient.setQueryData('auth-check', (old: any) => ({
          ...old,
          user: { ...(old?.user || {}), ...(data || {}) },
        }));
        toast.success('Perfil atualizado com sucesso!');
      },
      onError: (error: any) => {
        const errorMessage = ErrorHandler.handleApiError(error);
        toast.error(errorMessage);
      },
    }
  );
};

// Hook para atualizar perfil do cliente
export const useUpdateClienteProfile = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: ClienteProfile) => authApi.updateProfile(data),
    {
      onSuccess: (data) => {
        queryClient.setQueryData('auth-check', (old: any) => ({
          ...old,
          cliente: data,
        }));
        toast.success('Perfil do cliente atualizado com sucesso!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar perfil do cliente');
      },
    }
  );
};

// Hook para completar perfil do cliente
export const useCompleteClienteProfile = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (data: ClienteProfile) => authApi.completeClienteProfile(data),
    {
      onSuccess: (data) => {
        queryClient.setQueryData('auth-check', (old: any) => ({
          ...old,
          cliente: data,
        }));
        toast.success('Perfil completado com sucesso!');
        navigate('/my-recommendations');
      },
      onError: (error: any) => {
        const errorMessage = ErrorHandler.handleApiError(error);
        toast.error(errorMessage);
      },
    }
  );
};

// Hook para verificar se é concessionária
export const useIsConcessionaria = () => {
  const { data: authData } = useAuthCheck();
  const userData = getUserData();
  return (authData as any)?.user?.tipo === 'concessionaria' || userData?.tipo === 'concessionaria';
};

// Hook para verificar se é cliente
export const useIsCliente = () => {
  const { data: authData } = useAuthCheck();
  const userData = getUserData();
  return (authData as any)?.user?.tipo === 'cliente' || userData?.tipo === 'cliente';
};

// Hook para verificar se está autenticado
export const useIsAuthenticated = () => {
  const { data: authData } = useAuthCheck();
  return !!(authData as any)?.user || isAuthenticated();
};

// Hook para obter dados do usuário
export const useUser = () => {
  const { data: authData } = useAuthCheck();
  return (authData as any)?.user || getUserData();
};

// Hook para obter dados do cliente
export const useCliente = () => {
  const { data: authData } = useAuthCheck();
  return (authData as any)?.cliente || null;
};
